import { BALANCE, levelInfo, levelUnlocks, type BuildableModuleType, type StationState } from "@station-keeping/sim";
import { useGameUi } from "../game/store";
import { formatNumber, hasMessage, t } from "../i18n";
import { Dialog } from "./Dialog";
import { moduleName } from "./labels";
import { Icon, PixelText } from "./pixel";
import { Gauge, Key, Led, SectionTitle } from "./primitives";
import { ModuleThumb } from "./thumbs";

/** Pourquoi un module débloqué compte : la mécanique qu'il amène (seulement pour ceux qui changent le trafic ou le triage). */
function why(type: BuildableModuleType): string | null {
  const key = `level.why.${type}`;
  return hasMessage(key) ? t(key) : null;
}

/** Ce qu'apporte un niveau : nouveaux modules (avec leur raison d'être), limites relevées, anneau, améliorations. */
export function UnlockList({ level, detailed = false }: { level: number; detailed?: boolean }) {
  const unlocks = levelUnlocks(level);
  return (
    <ul className="flex flex-col">
      {unlocks.modules.map((type) => {
        const reason = detailed ? why(type) : null;
        return (
          <li key={type} className="dotted flex items-center gap-3 py-2.5">
            {detailed ? (
              <span className="grid w-[4.25rem] shrink-0 place-items-center">
                <ModuleThumb type={type} size="sm" />
              </span>
            ) : (
              <Led on color="var(--color-accent)" />
            )}
            <span className="min-w-0">
              <span className="block text-base font-semibold text-ink">{t("level.unlock.module", { module: moduleName(type) })}</span>
              {reason ? <span className="block text-[0.875rem] text-muted">{reason}</span> : null}
            </span>
          </li>
        );
      })}
      {unlocks.limits.map((limit) => (
        <li key={limit.type} className="dotted flex items-center gap-2.5 py-2 text-[0.9375rem] text-ink">
          <Led on color="var(--color-accent)" />
          {t("level.unlock.limit", { module: moduleName(limit.type), count: limit.to })}
        </li>
      ))}
      {unlocks.ring !== null ? (
        <li className="dotted flex items-center gap-2.5 py-2 text-[0.9375rem] text-ink">
          <Led on color="var(--color-accent)" />
          {t("level.unlock.ring", { ring: unlocks.ring })}
        </li>
      ) : null}
      {level > 1 ? (
        <li className="dotted flex items-center gap-2.5 py-2 text-[0.9375rem] text-ink">
          <Led on color="var(--color-accent)" />
          {t("level.unlock.upgrades", { level: Math.min(level, BALANCE.upgrades.maxLevel) })}
        </li>
      ) : null}
    </ul>
  );
}

/** La progression dans le bilan de la station : niveau, expérience vers le suivant, et ce qu'il apportera. */
export function ProgressionSection({ state }: { state: StationState }) {
  const info = levelInfo(state);
  const span = info.xpTo === null ? 1 : Math.max(1, info.xpTo - info.xpFrom);
  return (
    <section aria-label={t("station.level")}>
      <SectionTitle hint={t("level.howTo")}>{t("station.level")}</SectionTitle>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-accent">
          <Icon name="level" />
          <PixelText text={t("level.title", { level: info.level })} />
        </span>
        {info.xpTo !== null ? (
          <span className="text-ink">
            <PixelText text={t("level.xp", { xp: formatNumber(state.xp), next: formatNumber(info.xpTo) })} />
          </span>
        ) : null}
      </div>
      <Gauge
        value={info.xpTo === null ? 1 : (state.xp - info.xpFrom) / span}
        color="var(--color-accent)"
        label={t("station.level")}
        segments={16}
        className="mt-1.5"
      />
      {info.xpTo === null ? (
        <p className="mt-2 text-[0.9375rem] text-muted">{t("level.max")}</p>
      ) : (
        <>
          <p className="mt-3 text-[0.9375rem] text-dim">{t("level.next", { level: info.level + 1 })}</p>
          <UnlockList level={info.level + 1} />
        </>
      )}
    </section>
  );
}

/** La station vient de monter de niveau : ce qui s'ouvre, et pourquoi ça compte. */
export function LevelUpDialog({ level }: { level: number }) {
  const dismiss = useGameUi((ui) => ui.dismissLevelUp);
  return (
    <Dialog
      title={t("level.up.title", { level })}
      footer={
        <Key tone="action" onClick={dismiss} autoFocus>
          {t("level.up.close")}
        </Key>
      }
    >
      <p className="mt-3 text-base text-muted">{t("level.up.body")}</p>
      <div className="mt-2">
        <UnlockList level={level} detailed />
      </div>
    </Dialog>
  );
}
